import React from 'react';
import cx from 'classnames';
import { CheckIcon } from '@heroicons/react/20/solid';
import Step from 'ui-toolkit/images/icons/Step.svg?react';
import { BadgeTW } from 'tailwindComponents/badgeTW/BadgeTW';
import { Tooltip } from 'ui-toolkit';
import { isNil } from 'lodash';

export interface IStepperSubLabel {
  text?: string;
  tooltipText?: string;
  customStyles?: string;
}

export interface StepperProps {
  current: number;
  labels?: string[];
  subLabels?: IStepperSubLabel[];
}

export const StepperTW: React.FC<StepperProps> = ({ current, labels, subLabels }) => {
  const currentStep = current + 1;

  return (
    <div data-test-id="stepper-tw" className="mb-4 flex flex-col items-center justify-center gap-10">
      <div className="flex max-w-full justify-between">
        {labels?.map((step, i) => {
          const isActive = currentStep === i + 1;
          const isCompleted = currentStep > i + 1;
          const isNext = currentStep < i + 1;
          const notFirstChild = i > 0;
          const isLastChild = i === labels.length - 1;
          return (
            <div
              key={i}
              className={cx(`relative flex h-32 w-36 flex-col items-center`, {
                'pt-6': !subLabels?.length,
                'justify-center': !!subLabels?.length,
                [`before:absolute before:right-2/4 before:top-1/3 before:h-2 before:w-full before:-translate-y-3/4 before:bg-foundation-coolGray before:content-['']`]:
                  notFirstChild,
                'before:rounded-r-xl': isLastChild,
                'before:bg-foundation-zillowBlue': i > 0 && (isCompleted || isActive),
              })}
            >
              <div
                className={cx(
                  'relative z-10 flex h-8 w-3 items-center justify-center rounded-full font-semibold text-white',
                  {
                    'h-8 w-8 border-0 bg-white bg-opacity-0': isActive || isCompleted,
                    'pr-2': isLastChild,
                  }
                )}
              >
                {isCompleted && (
                  <div className="flex h-5 w-5 items-center justify-center rounded-full border-0 bg-foundation-zillowBlue text-white">
                    <CheckIcon className="flex h-3 w-3 justify-center" aria-hidden="true" />
                  </div>
                )}
                {isActive && <Step />}
                {isNext && <div className="mt-[1px] h-1 w-1 rounded-full border-gray-300 bg-white"></div>}
              </div>
              <>
                <p
                  className={cx({
                    'font-bold text-foundation-zillowBlue': isCompleted,
                    'font-bold text-black': isActive,
                    'text-gray-500': !isCompleted && !isActive,
                  })}
                >
                  {step}
                </p>
                {!!subLabels?.length && (
                  <section>
                    {subLabels?.[i]?.tooltipText && subLabels?.[i]?.text ? (
                      <Tooltip
                        tooltipContent={subLabels?.[i]?.tooltipText}
                        offset="0.5rem"
                        trigger="hover"
                        placement="bottom"
                      >
                        <BadgeTW className={getSubLabelStyles(subLabels?.[i], isActive, isCompleted)}>
                          <p className="font-bold">{subLabels?.[i]?.text}</p>
                        </BadgeTW>
                      </Tooltip>
                    ) : (
                      <BadgeTW className={getSubLabelStyles(subLabels?.[i], isActive, isCompleted)}>
                        <p className="font-bold">{subLabels?.[i]?.text}</p>
                      </BadgeTW>
                    )}
                  </section>
                )}
              </>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const getSubLabelStyles = (subLabel: IStepperSubLabel | undefined, isActive: boolean, isCompleted: boolean) => {
  let styles = !!subLabel?.text ? 'visible ' : 'collapse ';
  if (!isNil(subLabel?.customStyles)) {
    styles += subLabel!.customStyles;
  } else {
    styles += cx({
      'bg-gray-200 text-gray-800': isActive,
      'bg-green-100 text-green-800': isCompleted,
    });
  }
  return styles;
};
